import React, { useEffect, useMemo, useRef, useState } from 'react'
import ContextSelectorBar from '../components/ContextSelectorBar'
import CurrentContextBar from '../components/CurrentContextBar'
import SFRMatrix, {
  getSfrMatrixStatusPresentation,
  interpolateSFR,
  type SfrMatrixStatusPresentation
} from '../components/SFRMatrix'
import { MatrixData } from '../components/MatrixTable'
import GradientButton from '../components/GradientButton'
import { type ProductionSpeciesKey, loadStoredSpecies, persistSpeciesSelection } from '../data/productionSpeciesPresets'
import { useLots } from '../hooks/useLots'
import { useTwoStepConfirm } from '../hooks/useTwoStepConfirm'
import {
  cacheMatrix,
  cloneMatrix as cloneSfrMatrix,
  getDefaultMatrixForSpecies,
  interpolateFromMatrix,
  markLocalSfrMatrixEdit,
  resolveSfrMatrixPresetKey
} from '../utils/sfrMatrix'
import { loadActiveContextSelection, persistActiveContextSelection } from '../utils/activeContextSelection'
import { findFarmByIdOrName, findProductionUnitByIdOrName } from '../utils/contextSelection'
import { findLotByDashboardSelection, normalizeDashboardLotSelection } from '../utils/dashboardLotSelection'
import { saveEditedFarm } from '../utils/editFarmApi'
import { resolveFarmProductionSpeciesKey } from '../utils/farmProductionSpecies'
import { ensureFarmList, readCurrentFarm, type WizardFarm } from '../utils/farmWizard'
import { createId } from '../utils/id'
import { lotApi } from '../utils/lotApi'
import { isLotActiveForInteraction } from '../utils/lotInteraction'
import {
  findFarmSfrProfile,
  listFarmSfrProfiles,
  readAssignedSfrProfileId,
  readFarmDefaultSfrProfileId,
  resolveSfrProfileSelection,
  setFarmDefaultSfrProfileId,
  setLotSfrProfileAssignment,
  setProductionUnitSfrProfileAssignment,
  upsertFarmSfrProfile
} from '../utils/sfrProfiles'
import { useT, type TranslationKey } from '../i18n'

type CalculatorDebugInfo = {
  appliedEffectiveTemperature: number
  referenceEffectiveTemperature: number | null
  referenceSfr: number | null
}

const SPECIES_KEYS: ProductionSpeciesKey[] = ['trout', 'salmon', 'tilapia', 'shrimp']

const SPECIES_I18N: Record<(typeof SPECIES_KEYS)[number], { labelKey: TranslationKey; descriptionKey: TranslationKey }> = {
  trout: {
    labelKey: 'sfrManagement.species.trout.label',
    descriptionKey: 'sfrManagement.species.trout.description'
  },
  salmon: {
    labelKey: 'sfrManagement.species.salmon.label',
    descriptionKey: 'sfrManagement.species.salmon.description'
  },
  tilapia: {
    labelKey: 'sfrManagement.species.tilapia.label',
    descriptionKey: 'sfrManagement.species.tilapia.description'
  },
  shrimp: {
    labelKey: 'sfrManagement.species.shrimp.label',
    descriptionKey: 'sfrManagement.species.shrimp.description'
  }
}

const PROFILE_EDITOR_SPECIES_DEFAULT = '__species_default__'
const PROFILE_SAVE_DEBOUNCE_MS = 600
// resetToDefaults has two branches (with/without a selectedProfile) that
// share the same physical "Reset" button — a single sentinel id arms both.
const RESET_CONFIRM_ID = 'sfr-reset-to-defaults'

const matricesEqual = (left: MatrixData, right: MatrixData): boolean => JSON.stringify(left) === JSON.stringify(right)

const sfrStatusStyle = (tone: SfrMatrixStatusPresentation['tone']): React.CSSProperties => {
  const borderColor = tone === 'success'
    ? '#86efac'
    : tone === 'error'
      ? '#fecaca'
      : '#bae6fd'
  const backgroundColor = tone === 'success'
    ? '#f0fdf4'
    : tone === 'error'
      ? '#fef2f2'
      : '#f0f9ff'
  const textColor = tone === 'success'
    ? '#166534'
    : tone === 'error'
      ? '#b91c1c'
      : '#075985'

  return {
    marginBottom: '20px',
    padding: '12px 16px',
    borderRadius: '10px',
    border: `1px solid ${borderColor}`,
    background: backgroundColor,
    color: textColor,
    fontSize: '14px',
    fontWeight: 500,
    lineHeight: '1.5'
  }
}

export const resolveSfrManagementSpecies = (
  farm: WizardFarm | undefined,
  fallbackSpecies: ProductionSpeciesKey
): ProductionSpeciesKey => {
  const farmSpeciesKey = resolveFarmProductionSpeciesKey(farm)
  return farmSpeciesKey ?? fallbackSpecies
}

// PR-alpha4a U8+U10: `resetToDefaults` two-step confirm decision, extracted
// as a pure function (mirroring `resolveHarvestTransitionOutcome` in
// Biomass.tsx) so both branches — with/without a `selectedProfile` — are
// unit-testable without mounting the full `SFRManagement` screen, which has
// a data-bootstrap surface unrelated to this decision.
export type SfrResetToDefaultsAction = 'arm' | 'applyProfileDefaults' | 'applySpeciesDefaults'

export const resolveSfrResetToDefaultsAction = (
  isConfirmArmed: boolean,
  hasSelectedProfile: boolean
): SfrResetToDefaultsAction => {
  if (!isConfirmArmed) return 'arm'
  return hasSelectedProfile ? 'applyProfileDefaults' : 'applySpeciesDefaults'
}

export default function SFRManagement() {
  const t = useT()
  const [testWeight, setTestWeight] = useState<string>('100')
  const [testTemperature, setTestTemperature] = useState<string>('15')
  const [calculatedSFR, setCalculatedSFR] = useState<number | null>(null)
  const [calculatorDebugInfo, setCalculatorDebugInfo] = useState<CalculatorDebugInfo | null>(null)
  const [currentSFRData, setCurrentSFRData] = useState<MatrixData | null>(null)
  const [selectedSpecies, setSelectedSpecies] = useState<ProductionSpeciesKey>(() => loadStoredSpecies())
  const [selectedProfileId, setSelectedProfileId] = useState<string>(PROFILE_EDITOR_SPECIES_DEFAULT)
  const [availableFarms, setAvailableFarms] = useState<WizardFarm[]>([])
  const [farmId, setFarmId] = useState<string>('')
  const [productionUnitId, setProductionUnitId] = useState<string>('')
  const [lotId, setLotId] = useState<string>('')
  const [refreshKey, setRefreshKey] = useState<number>(0)
  const [matrixVersion, setMatrixVersion] = useState<number>(0)
  const [newWeight, setNewWeight] = useState<string>('')
  const [newTemperature, setNewTemperature] = useState<string>('')
  const [matrixStatus, setMatrixStatus] = useState<SfrMatrixStatusPresentation | null>(null)
  const profileSaveTimeoutRef = useRef<ReturnType<typeof setTimeout>>()
  const skipNextProfileSaveRef = useRef(true)

  const selectedFarm = useMemo(() => findFarmByIdOrName(availableFarms, farmId), [availableFarms, farmId])
  const availableProductionUnits = useMemo(() => selectedFarm?.productionUnits ?? [], [selectedFarm])
  const selectedProductionUnit = useMemo(
    () => findProductionUnitByIdOrName(selectedFarm, productionUnitId),
    [selectedFarm, productionUnitId]
  )
  const {
    lots,
    loading: lotsLoading,
    upsertLot
  } = useLots({ farmId, productionUnitId, enabled: Boolean(farmId) })
  const selectedLot = useMemo(
    () => findLotByDashboardSelection(lotId, lots) ?? undefined,
    [lotId, lots]
  )
  const selectedLotIsInteractive = selectedLot ? isLotActiveForInteraction(selectedLot.stage) : false

  const selectedPresetKey = useMemo(() => resolveSfrMatrixPresetKey(selectedSpecies), [selectedSpecies])
  const facilityProfiles = useMemo(() => listFarmSfrProfiles(selectedFarm, selectedSpecies), [selectedFarm, selectedSpecies])
  const selectedProfile = useMemo(
    () => facilityProfiles.find((profile) => profile.id === selectedProfileId) ?? null,
    [facilityProfiles, selectedProfileId]
  )
  const appliedProfileSelection = useMemo(
    () => resolveSfrProfileSelection({
      farm: selectedFarm,
      productionUnit: selectedProductionUnit,
      lot: selectedLot,
      species: selectedSpecies
    }),
    [selectedFarm, selectedLot, selectedProductionUnit, selectedSpecies]
  )
  const facilityDefaultProfile = useMemo(
    () => findFarmSfrProfile(selectedFarm, readFarmDefaultSfrProfileId(selectedFarm, selectedSpecies)),
    [selectedFarm, selectedSpecies]
  )
  const productionUnitAssignedProfile = useMemo(
    () => findFarmSfrProfile(selectedFarm, readAssignedSfrProfileId(selectedProductionUnit, selectedSpecies)),
    [selectedFarm, selectedProductionUnit, selectedSpecies]
  )
  const lotAssignedProfile = useMemo(
    () => findFarmSfrProfile(selectedFarm, readAssignedSfrProfileId(selectedLot, selectedSpecies)),
    [selectedFarm, selectedLot, selectedSpecies]
  )
  const isEditingFacilityProfile = Boolean(selectedProfile)

  const speciesOptions = useMemo(
    () => SPECIES_KEYS.map((value) => ({ value, label: t(SPECIES_I18N[value].labelKey) })),
    [t]
  )

  const profileOptions = useMemo(
    () => [
      {
        value: PROFILE_EDITOR_SPECIES_DEFAULT,
        label: t('sfrManagement.profile.option.speciesDefault')
      },
      ...facilityProfiles.map((profile) => ({
        value: profile.id,
        label: profile.name
      }))
    ],
    [facilityProfiles, t]
  )

  const selectedSpeciesDescription = useMemo(() => {
    const entry = SPECIES_I18N[selectedSpecies]
    return entry ? t(entry.descriptionKey) : t('sfrManagement.species.descriptionFallback')
  }, [selectedSpecies, t])

  const selectedSpeciesLabel = useMemo(() => {
    const option = speciesOptions.find((item) => item.value === selectedSpecies)
    return option?.label ?? selectedSpecies
  }, [selectedSpecies, speciesOptions])

  const selectedFarmLabel = useMemo(() => {
    if (!farmId) return '—'
    return selectedFarm?.name || String(farmId)
  }, [farmId, selectedFarm])

  const selectedProductionUnitLabel = useMemo(() => {
    if (!selectedFarm) return '—'
    if (!productionUnitId) return t('warehouse.allUnits')
    return selectedProductionUnit?.name || String(productionUnitId)
  }, [productionUnitId, selectedFarm, selectedProductionUnit, t])

  const selectedLotLabel = useMemo(() => {
    if (!productionUnitId) return t('tasks.filter.allLots')
    if (!lotId) return t('tasks.filter.allLots')
    return selectedLot?.lotKey || String(lotId)
  }, [lotId, productionUnitId, selectedLot, t])

  const selectedMatrixLabel = useMemo(
    () => selectedProfile?.name ?? t('sfrManagement.profile.option.speciesDefault'),
    [selectedProfile, t]
  )

  const profileEditingSummary = useMemo(
    () => selectedProfile
      ? t('sfrManagement.profile.summary.editingProfile', { name: selectedProfile.name })
      : t('sfrManagement.profile.summary.editingSpeciesDefault', { species: selectedSpeciesLabel }),
    [selectedProfile, selectedSpeciesLabel, t]
  )

  const profileAppliedSummary = useMemo(() => {
    switch (appliedProfileSelection.source) {
      case 'lot':
        return t('sfrManagement.profile.summary.appliedLot', {
          profile: appliedProfileSelection.profile?.name ?? t('sfrManagement.profile.option.speciesDefault'),
          lot: selectedLotLabel
        })
      case 'production-unit':
        return t('sfrManagement.profile.summary.appliedProductionUnit', {
          profile: appliedProfileSelection.profile?.name ?? t('sfrManagement.profile.option.speciesDefault'),
          productionUnit: selectedProductionUnitLabel
        })
      case 'facility-default':
        return t('sfrManagement.profile.summary.appliedFacilityDefault', {
          profile: appliedProfileSelection.profile?.name ?? t('sfrManagement.profile.option.speciesDefault'),
          facility: selectedFarmLabel
        })
      default:
        return t('sfrManagement.profile.summary.appliedSpeciesDefault', { species: selectedSpeciesLabel })
    }
  }, [
    appliedProfileSelection.profile?.name,
    appliedProfileSelection.source,
    selectedFarmLabel,
    selectedLotLabel,
    selectedProductionUnitLabel,
    selectedSpeciesLabel,
    t
  ])

  const profileHelpText = useMemo(
    () => selectedFarm
      ? t('sfrManagement.profile.help.facility')
      : t('sfrManagement.profile.help.noFacility'),
    [selectedFarm, t]
  )

  const farmOptions = useMemo(
    () => availableFarms.map((farm) => ({ value: String(farm.id), label: farm.name || String(farm.id), key: String(farm.id) })),
    [availableFarms]
  )

  const productionUnitOptions = useMemo(
    () => availableProductionUnits.map((unit) => ({ value: String(unit.id), label: unit.name || String(unit.id), key: String(unit.id) })),
    [availableProductionUnits]
  )

  const lotOptions = useMemo(
    () => lots.map((lot) => ({
      value: String(lot.id),
      label: isLotActiveForInteraction(lot.stage)
        ? (lot.lotKey || String(lot.id))
        : `${lot.lotKey || String(lot.id)} (${t('lots.stage.inactive')})`,
      key: String(lot.id),
      disabled: !isLotActiveForInteraction(lot.stage)
    })),
    [lots, t]
  )

  const lotPlaceholder = useMemo(() => {
    if (productionUnitId) {
      if (lotsLoading) return t('biomass.lot.placeholder.loadingLots')
      if (lotOptions.length === 0) return t('home.selector.lot.placeholder.noneForUnit')
      return t('tasks.filter.allLots')
    }

    return t('home.selector.lot.placeholder.selectUnitFirst')
  }, [lotOptions.length, lotsLoading, productionUnitId, t])

  useEffect(() => {
    let cancelled = false

    const loadContext = async () => {
      const farms = await ensureFarmList()
      if (cancelled) return

      setAvailableFarms(farms)

      const activeContext = loadActiveContextSelection()
      const rememberedFarm = readCurrentFarm()
      const resolvedFarm = findFarmByIdOrName(farms, activeContext.farmId)
        ?? findFarmByIdOrName(farms, rememberedFarm?.id ?? rememberedFarm?.name ?? '')
      const resolvedUnit = findProductionUnitByIdOrName(resolvedFarm, activeContext.productionUnitId)
      const storedSpecies = loadStoredSpecies()
      const nextSpecies = resolveSfrManagementSpecies(resolvedFarm, storedSpecies)

      setFarmId(resolvedFarm?.id ? String(resolvedFarm.id) : '')
      setProductionUnitId(resolvedUnit?.id ? String(resolvedUnit.id) : '')
      setLotId(activeContext.lotId?.trim() ?? '')
      setSelectedSpecies(nextSpecies)
      persistSpeciesSelection(nextSpecies)
    }

    void loadContext()

    return () => {
      cancelled = true
    }
  }, [])

  useEffect(() => {
    persistSpeciesSelection(selectedSpecies)
  }, [selectedSpecies])

  useEffect(() => {
    if (!productionUnitId && lotId) {
      setLotId('')
      persistActiveContextSelection({ lotId: '' })
      return
    }

    if (lotId && !lotsLoading) {
      const normalizedLotId = normalizeDashboardLotSelection(lotId, lots)
      if (!normalizedLotId) {
        setLotId('')
        persistActiveContextSelection({ lotId: '' })
        return
      }

      if (normalizedLotId !== lotId) {
        setLotId(normalizedLotId)
        persistActiveContextSelection({ lotId: normalizedLotId })
      }
    }
  }, [lotId, lots, lotsLoading, productionUnitId])

  useEffect(() => () => {
    if (profileSaveTimeoutRef.current) {
      globalThis.clearTimeout(profileSaveTimeoutRef.current)
    }
  }, [])

  useEffect(() => {
    if (selectedProfileId !== PROFILE_EDITOR_SPECIES_DEFAULT && facilityProfiles.some((profile) => profile.id === selectedProfileId)) {
      return
    }

    setSelectedProfileId(appliedProfileSelection.profileId ?? PROFILE_EDITOR_SPECIES_DEFAULT)
  }, [appliedProfileSelection.profileId, facilityProfiles, selectedProfileId])

  useEffect(() => {
    skipNextProfileSaveRef.current = true
    setCalculatedSFR(null)
    setCalculatorDebugInfo(null)

    if (!selectedProfile) {
      setCurrentSFRData(null)
      setMatrixVersion((prev) => prev + 1)
      return
    }

    setCurrentSFRData(cloneSfrMatrix(selectedProfile.matrix))
    setMatrixVersion((prev) => prev + 1)
  }, [selectedProfile?.id, selectedProfile?.updatedAt, selectedSpecies])

  const setSaveErrorStatus = () => {
    setMatrixStatus({
      state: 'error',
      tone: 'error',
      message: t('sfrManagement.status.saveFailed')
    })
  }

  const showSyncStatus = (syncState: 'offline' | 'pending' | 'synced') => {
    setMatrixStatus(getSfrMatrixStatusPresentation(syncState, (key) => t(key as TranslationKey), null))
  }

  const handleMutationStatus = (result: {
    syncState: 'offline' | 'pending' | 'synced'
    syncPromise: Promise<'offline' | 'pending' | 'synced'> | null
  }) => {
    showSyncStatus(result.syncState)

    if (!result.syncPromise) {
      return
    }

    void result.syncPromise
      .then((nextSyncState) => {
        showSyncStatus(nextSyncState)
      })
      .catch((error) => {
        console.error('Failed to sync SFR profile mutation', error)
        setSaveErrorStatus()
      })
  }

  const mergeSavedFarm = (savedFarm: WizardFarm) => {
    setAvailableFarms((prev) => {
      const hasExisting = prev.some((farm) => String(farm.id ?? '') === String(savedFarm.id ?? ''))
      if (!hasExisting) {
        return [...prev, savedFarm]
      }

      return prev.map((farm) => String(farm.id ?? '') === String(savedFarm.id ?? '') ? savedFarm : farm)
    })
  }

  const persistFarmMutation = async (baseFarm: WizardFarm, nextFarm: WizardFarm): Promise<WizardFarm | null> => {
    try {
      const result = await saveEditedFarm(nextFarm, baseFarm)
      mergeSavedFarm(result.data)
      handleMutationStatus(result)
      return result.data
    } catch (error) {
      console.error('Failed to save SFR profile metadata', error)
      setSaveErrorStatus()
      return null
    }
  }

  const handleProfileChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    setSelectedProfileId(event.target.value)
    setCalculatedSFR(null)
    setCalculatorDebugInfo(null)
    setMatrixStatus(null)
  }

  const handleSFRChange = (sfrData: MatrixData) => {
    setCurrentSFRData(sfrData)
  }

  useEffect(() => {
    if (!selectedFarm || !selectedProfile || !currentSFRData) {
      return
    }

    if (skipNextProfileSaveRef.current) {
      skipNextProfileSaveRef.current = false
      return
    }

    if (matricesEqual(currentSFRData, selectedProfile.matrix)) {
      return
    }

    if (profileSaveTimeoutRef.current) {
      globalThis.clearTimeout(profileSaveTimeoutRef.current)
    }

    setMatrixStatus(getSfrMatrixStatusPresentation('saving', (key) => t(key as TranslationKey), null))

    profileSaveTimeoutRef.current = globalThis.setTimeout(async () => {
      const nextProfile = {
        ...selectedProfile,
        matrix: cloneSfrMatrix(currentSFRData),
        updatedAt: new Date().toISOString()
      }
      const nextFarm = upsertFarmSfrProfile(selectedFarm, nextProfile)
      await persistFarmMutation(selectedFarm, nextFarm)
    }, PROFILE_SAVE_DEBOUNCE_MS)

    return () => {
      if (profileSaveTimeoutRef.current) {
        globalThis.clearTimeout(profileSaveTimeoutRef.current)
      }
    }
  }, [currentSFRData, selectedFarm, selectedProfile, t])

  const handleSpeciesChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    const nextSpecies = event.target.value as ProductionSpeciesKey
    const nextPresetKey = resolveSfrMatrixPresetKey(nextSpecies)
    const presetChanged = nextPresetKey !== selectedPresetKey
    setSelectedSpecies(nextSpecies)
    setSelectedProfileId(PROFILE_EDITOR_SPECIES_DEFAULT)
    setMatrixStatus(null)
    persistSpeciesSelection(nextSpecies)

    if (presetChanged) {
      setCurrentSFRData(null)
      setCalculatedSFR(null)
      setCalculatorDebugInfo(null)
      setMatrixVersion((prev) => prev + 1)
    }
  }

  const handleFarmChange = (value: string) => {
    const nextFarm = findFarmByIdOrName(availableFarms, value)
    const nextSpecies = nextFarm ? resolveSfrManagementSpecies(nextFarm, selectedSpecies) : selectedSpecies

    setFarmId(value)
    setProductionUnitId('')
    setLotId('')
    setSelectedSpecies(nextSpecies)
    setSelectedProfileId(PROFILE_EDITOR_SPECIES_DEFAULT)
    setMatrixStatus(null)
    persistSpeciesSelection(nextSpecies)
    persistActiveContextSelection({ farmId: value, productionUnitId: '', lotId: '' })
  }

  const handleProductionUnitChange = (value: string) => {
    setProductionUnitId(value)
    setLotId('')
    setSelectedProfileId(PROFILE_EDITOR_SPECIES_DEFAULT)
    setMatrixStatus(null)
    persistActiveContextSelection({ productionUnitId: value, lotId: '' })
  }

  const handleLotChange = (value: string) => {
    setLotId(value)
    setSelectedProfileId(PROFILE_EDITOR_SPECIES_DEFAULT)
    setMatrixStatus(null)
    persistActiveContextSelection({ lotId: value })
  }

  const handleCreateFacilityProfile = async () => {
    if (!selectedFarm) {
      alert(t('sfrManagement.profile.alert.selectFacility'))
      return
    }

    if (!currentSFRData) {
      return
    }

    const suggestedName = [selectedFarm.name, selectedProductionUnit?.name, selectedLot?.lotKey, selectedSpeciesLabel]
      .map((value) => String(value ?? '').trim())
      .filter(Boolean)
      .join(' - ')
    const nextName = window.prompt(t('sfrManagement.profile.prompt.name'), suggestedName)?.trim()

    if (!nextName) {
      return
    }

    const timestamp = new Date().toISOString()
    const nextProfile = {
      id: createId('sfr-profile'),
      species: selectedSpecies,
      name: nextName,
      matrix: cloneSfrMatrix(currentSFRData),
      createdAt: timestamp,
      updatedAt: timestamp
    }
    const nextFarm = upsertFarmSfrProfile(selectedFarm, nextProfile)
    const savedFarm = await persistFarmMutation(selectedFarm, nextFarm)

    if (!savedFarm) {
      return
    }

    setSelectedProfileId(nextProfile.id)
  }

  const handleSetFacilityDefault = async (profileId: string | null) => {
    if (!selectedFarm) {
      alert(t('sfrManagement.profile.alert.selectFacility'))
      return
    }

    const nextFarm = setFarmDefaultSfrProfileId(selectedFarm, selectedSpecies, profileId)
    await persistFarmMutation(selectedFarm, nextFarm)
  }

  const handleApplyToProductionUnit = async (profileId: string | null) => {
    if (!selectedFarm) {
      alert(t('sfrManagement.profile.alert.selectFacility'))
      return
    }

    if (!selectedProductionUnit) {
      alert(t('sfrManagement.profile.alert.selectProductionUnit'))
      return
    }

    const nextFarm = {
      ...selectedFarm,
      productionUnits: (selectedFarm.productionUnits ?? []).map((unit) => (
        String(unit.id ?? '') === String(selectedProductionUnit.id ?? '')
          ? setProductionUnitSfrProfileAssignment(unit, selectedSpecies, profileId)
          : unit
      ))
    }

    await persistFarmMutation(selectedFarm, nextFarm)
  }

  const handleApplyToLot = async (profileId: string | null) => {
    if (!selectedLot) {
      alert(t('sfrManagement.profile.alert.selectLot'))
      return
    }

    if (!selectedLotIsInteractive) {
      alert(t('sfrManagement.profile.alert.activeLotRequired'))
      return
    }

    const nextLot = setLotSfrProfileAssignment(selectedLot, selectedSpecies, profileId)

    try {
      const result = await lotApi.update(selectedLot.id, { metadata: nextLot.metadata })
      upsertLot(result.data)
      handleMutationStatus(result)
    } catch (error) {
      console.error('Failed to save SFR lot profile assignment', error)
      setSaveErrorStatus()
    }
  }

  const calculateTestSFR = () => {
    if (!currentSFRData) return
    
    const weight = Number.parseFloat(testWeight)
    const temperature = Number.parseFloat(testTemperature)
    
    if (Number.isNaN(weight) || Number.isNaN(temperature)) {
      alert(t('sfrManagement.alert.invalidWeightTemp'))
      return
    }

    const currentPathResult = interpolateFromMatrix(currentSFRData, weight, temperature, { species: selectedPresetKey })
    const selectedPresetReference = interpolateFromMatrix(currentSFRData, weight, temperature, { species: selectedPresetKey })
    const sfr = interpolateSFR(weight, temperature, currentSFRData, selectedPresetKey)

    setCalculatorDebugInfo({
      appliedEffectiveTemperature: currentPathResult.clampedTemp ?? temperature,
      referenceEffectiveTemperature: selectedPresetReference.clampedTemp ?? temperature,
      referenceSfr: Number.isFinite(selectedPresetReference.sfr) ? selectedPresetReference.sfr : null
    })
    setCalculatedSFR(sfr)
  }

  // v2.1 finding #83: iOS Chrome silently suppresses `window.confirm()` in
  // some event-handler contexts (WebKit dialog gating). Migrated to
  // `useTwoStepConfirm` (src/hooks/useTwoStepConfirm.ts) — two-step in-app
  // confirm pattern established in LotCostSection.tsx:142 and adopted in
  // Feed.tsx and Biomass.tsx α3b.
  const resetConfirm = useTwoStepConfirm()

  const resetConfirmMessage = useMemo(() => {
    if (selectedProfile) {
      return t('sfrManagement.profile.confirm.resetProfile', {
        profile: selectedProfile.name,
        species: selectedSpeciesLabel
      })
    }
    const sharedPresetNote =
      selectedPresetKey === 'global' && (selectedSpecies === 'trout' || selectedSpecies === 'salmon')
        ? t('sfrManagement.confirm.sharedPresetNote')
        : ''
    return t('sfrManagement.confirm.resetDefaults', { sharedPresetNote })
  }, [selectedProfile, selectedSpeciesLabel, selectedPresetKey, selectedSpecies, t])

  const resetToDefaults = () => {
    const action = resolveSfrResetToDefaultsAction(resetConfirm.isPending(RESET_CONFIRM_ID), Boolean(selectedProfile))

    if (action === 'arm') {
      resetConfirm.arm(RESET_CONFIRM_ID)
      return
    }

    if (action === 'applyProfileDefaults') {
      skipNextProfileSaveRef.current = false
      const defaults = getDefaultMatrixForSpecies(selectedPresetKey)
      setCurrentSFRData(defaults)
      setMatrixVersion((prev) => prev + 1)
      setCalculatedSFR(null)
      setCalculatorDebugInfo(null)
      resetConfirm.reset()
      return
    }

    const defaults = getDefaultMatrixForSpecies(selectedPresetKey)
    cacheMatrix(selectedPresetKey, defaults)
    markLocalSfrMatrixEdit(selectedPresetKey)
    setCurrentSFRData(defaults)
    setMatrixVersion((prev) => prev + 1)
    setCalculatedSFR(null)
    setCalculatorDebugInfo(null)
    resetConfirm.reset()
  }

  const refreshMatrix = () => {
    if (selectedProfile) {
      skipNextProfileSaveRef.current = true
      setCurrentSFRData(cloneSfrMatrix(selectedProfile.matrix))
      setMatrixVersion((prev) => prev + 1)
      setCalculatedSFR(null)
      setCalculatorDebugInfo(null)
      setMatrixStatus(null)
      return
    }

    setRefreshKey((prev) => prev + 1)
  }

  const updateMatrix = (updater: (matrix: MatrixData) => MatrixData | null) => {
    if (!currentSFRData) return
    const next = updater(currentSFRData)
    if (!next) return
    if (!isEditingFacilityProfile) {
      cacheMatrix(selectedPresetKey, next)
      markLocalSfrMatrixEdit(selectedPresetKey)
    }
    setCurrentSFRData(next)
    setMatrixVersion((prev) => prev + 1)
  }

  const handleAddWeight = () => {
    if (!currentSFRData) return
    const parsed = Number(newWeight.replace(',', '.'))
    if (!Number.isFinite(parsed) || parsed <= 0) {
      alert(t('sfrManagement.alert.invalidNewWeight'))
      return
    }
    if (currentSFRData.weights.includes(parsed)) {
      alert(t('sfrManagement.alert.weightExists'))
      return
    }
    updateMatrix((matrix) => {
      const cloned = cloneSfrMatrix(matrix)
      const insertIndex = cloned.weights.findIndex((value) => value > parsed)
      const targetIndex = insertIndex === -1 ? cloned.weights.length : insertIndex
      const referenceIndex = targetIndex === 0 ? 0 : targetIndex - 1
      const templateRow = cloned.values[referenceIndex] ?? cloned.values[cloned.values.length - 1]
      const newRow = templateRow ? [...templateRow] : cloned.temperatures.map(() => 0)
      cloned.weights.splice(targetIndex, 0, parsed)
      cloned.values.splice(targetIndex, 0, newRow)
      return cloned
    })
    setNewWeight('')
  }

  const handleRemoveWeight = (index: number) => {
    if (!currentSFRData || currentSFRData.weights.length <= 1) {
      alert(t('sfrManagement.alert.minWeightRows'))
      return
    }
    updateMatrix((matrix) => {
      const cloned = cloneSfrMatrix(matrix)
      cloned.weights.splice(index, 1)
      cloned.values.splice(index, 1)
      return cloned
    })
  }

  const handleAddTemperature = () => {
    if (!currentSFRData) return
    const parsed = Number(newTemperature.replace(',', '.'))
    if (!Number.isFinite(parsed)) {
      alert(t('sfrManagement.alert.invalidNewTemperature'))
      return
    }
    if (currentSFRData.temperatures.includes(parsed)) {
      alert(t('sfrManagement.alert.temperatureExists'))
      return
    }
    updateMatrix((matrix) => {
      const cloned = cloneSfrMatrix(matrix)
      const insertIndex = cloned.temperatures.findIndex((value) => value > parsed)
      const targetIndex = insertIndex === -1 ? cloned.temperatures.length : insertIndex
      const referenceIndex = targetIndex === 0 ? 0 : targetIndex - 1
      cloned.temperatures.splice(targetIndex, 0, parsed)
      cloned.values = cloned.values.map((row) => {
        const reference = row[referenceIndex] ?? row[row.length - 1] ?? 0
        const newRow = [...row]
        newRow.splice(targetIndex, 0, reference)
        return newRow
      })
      return cloned
    })
    setNewTemperature('')
  }

  const handleRemoveTemperature = (index: number) => {
    if (!currentSFRData || currentSFRData.temperatures.length <= 1) {
      alert(t('sfrManagement.alert.minTemperatureColumns'))
      return
    }
    updateMatrix((matrix) => {
      const cloned = cloneSfrMatrix(matrix)
      cloned.temperatures.splice(index, 1)
      cloned.values = cloned.values.map((row) => {
        const nextRow = [...row]
        nextRow.splice(index, 1)
        return nextRow
      })
      return cloned
    })
  }

  const sortedWeights = useMemo(() => currentSFRData?.weights ?? [], [currentSFRData])
  const sortedTemperatures = useMemo(
    () => currentSFRData?.temperatures ?? [],
    [currentSFRData]
  )

  const exportSFRData = () => {
    if (!currentSFRData) return
    
    const csvContent = [
      [String.raw`Weight\Temperature`, ...currentSFRData.temperatures.map((t: number) => `${t}°C`)].join(','),
      ...currentSFRData.weights.map((weight: number, rowIndex: number) => 
        [
          `${weight}g`,
          ...currentSFRData.values[rowIndex].map((v: number) => v.toString().replace('.', ','))
        ].join(',')
      )
    ].join('\n')
    
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    const sanitizedProfileName = selectedProfile?.name
      ?.toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '')
    a.download = sanitizedProfileName
      ? `sfr-matrix-${selectedSpecies}-${sanitizedProfileName}.csv`
      : `sfr-matrix-${selectedSpecies}.csv`
    document.body.appendChild(a)
    a.click()
    a.remove()
    URL.revokeObjectURL(url)
  }

  return (
    <div data-screen="sfr" className="responsive-container">
      <div className="app-header" style={{ padding: 0 }}>
        <h1>🧾 {t('sfrManagement.title')}</h1>
        <p>{t('sfrManagement.subtitle')}</p>
      </div>

      <CurrentContextBar
        style={{ marginTop: 14 }}
        species={{ value: selectedSpeciesLabel, label: t('sfrManagement.speciesPreset.label') }}
        fields={[
          { label: t('warehouse.contextFacility'), value: selectedFarmLabel },
          { label: t('warehouse.contextProductionUnit'), value: selectedProductionUnitLabel },
          { label: t('tasks.context.lot'), value: selectedLotLabel }
        ]}
      />

      <div className="form-card" style={{ marginTop: 14, marginBottom: 20 }}>
        <ContextSelectorBar
          farm={{
            label: t('warehouse.contextFacility'),
            value: farmId,
            options: farmOptions,
            onChange: handleFarmChange,
            placeholder: availableFarms.length === 0 ? t('warehouse.loadingFacilities') : t('warehouse.selectFacility'),
            disabled: availableFarms.length === 0
          }}
          productionUnit={{
            label: t('warehouse.contextProductionUnitOptional'),
            value: productionUnitId,
            options: productionUnitOptions,
            onChange: handleProductionUnitChange,
            placeholder: selectedFarm ? t('warehouse.allUnits') : t('warehouse.selectFacilityFirst'),
            disabled: !selectedFarm
          }}
          lot={{
            label: t('tasks.context.lot'),
            value: lotId,
            options: lotOptions,
            onChange: handleLotChange,
            placeholder: lotPlaceholder,
            disabled: !productionUnitId || lotsLoading
          }}
          summary={
            <div style={{ fontSize: 12, color: '#6b7280', paddingBottom: 8 }}>
              {selectedSpeciesDescription}
            </div>
          }
        />
      </div>

      <div className="responsive-toolbar">
        <div className="field" style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          <label style={{ fontSize: 12, fontWeight: 600, color: '#374151' }}>{t('sfrManagement.speciesPreset.label')}</label>
          <select
            value={selectedSpecies}
            onChange={handleSpeciesChange}
            style={{
              width: '100%',
              padding: '10px 12px',
              border: '1px solid #d1d5db',
              borderRadius: '8px',
              fontSize: '14px'
            }}
          >
            {speciesOptions.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
          <div style={{ fontSize: 12, color: '#6b7280' }}>
            {selectedSpeciesDescription}
          </div>
        </div>

        <div className="responsive-actions">
          <GradientButton onClick={refreshMatrix} variant="info" size="sm">
            {t('sfrManagement.action.refreshMatrix')}
          </GradientButton>

          <GradientButton onClick={exportSFRData} variant="success" size="sm" disabled={!currentSFRData}>
            {t('sfrManagement.action.exportCsv')}
          </GradientButton>

          <GradientButton
            onClick={resetToDefaults}
            variant="danger"
            size="sm"
            title={resetConfirm.isPending(RESET_CONFIRM_ID) ? resetConfirmMessage : undefined}
          >
            {resetConfirm.isPending(RESET_CONFIRM_ID)
              ? t('sfrManagement.confirm.resetCta')
              : t('sfrManagement.action.resetDefaults')}
          </GradientButton>
          {resetConfirm.isPending(RESET_CONFIRM_ID) ? (
            <GradientButton onClick={() => resetConfirm.reset()} variant="secondary" size="sm">
              {t('common.cancel')}
            </GradientButton>
          ) : null}
        </div>
      </div>

      <div className="form-card" style={{ marginBottom: 20 }}>
        <div style={{ display: 'grid', gap: 14 }}>
          <div style={{ display: 'grid', gap: 6 }}>
            <label style={{ fontSize: 12, fontWeight: 600, color: '#374151' }}>
              {t('sfrManagement.profile.label')}
            </label>
            <select
              value={selectedProfileId}
              onChange={handleProfileChange}
              disabled={!selectedFarm}
              style={{
                width: '100%',
                padding: '10px 12px',
                border: '1px solid #d1d5db',
                borderRadius: '8px',
                fontSize: '14px',
                backgroundColor: selectedFarm ? '#ffffff' : '#f9fafb'
              }}
            >
              {profileOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
            <div style={{ fontSize: 12, color: '#6b7280' }}>
              {profileHelpText}
            </div>
          </div>

          <div
            style={{
              display: 'grid',
              gap: 4,
              padding: '12px 14px',
              borderRadius: '10px',
              background: '#f8fafc',
              border: '1px solid #e2e8f0',
              fontSize: '12px',
              color: '#475569'
            }}
          >
            <div style={{ fontWeight: 600, color: '#0f172a' }}>{selectedMatrixLabel}</div>
            <div>{profileEditingSummary}</div>
            <div>{profileAppliedSummary}</div>
            {facilityDefaultProfile && (
              <div>
                {t('sfrManagement.profile.summary.facilityDefaultHint', { profile: facilityDefaultProfile.name })}
              </div>
            )}
            {selectedProductionUnit && productionUnitAssignedProfile && (
              <div>
                {t('sfrManagement.profile.summary.productionUnitHint', {
                  profile: productionUnitAssignedProfile.name,
                  productionUnit: selectedProductionUnitLabel
                })}
              </div>
            )}
            {selectedLot && lotAssignedProfile && (
              <div>
                {t('sfrManagement.profile.summary.lotHint', {
                  profile: lotAssignedProfile.name,
                  lot: selectedLotLabel
                })}
              </div>
            )}
          </div>

          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
            <GradientButton
              onClick={handleCreateFacilityProfile}
              variant="secondary"
              size="sm"
              disabled={!selectedFarm || !currentSFRData}
            >
              {t('sfrManagement.profile.action.saveAsFacilityProfile')}
            </GradientButton>

            <GradientButton
              onClick={() => handleSetFacilityDefault(selectedProfile ? selectedProfile.id : null)}
              variant="info"
              size="sm"
              disabled={selectedProfile ? !selectedFarm : !selectedFarm || !facilityDefaultProfile}
            >
              {selectedProfile
                ? t('sfrManagement.profile.action.setFacilityDefault')
                : t('sfrManagement.profile.action.clearFacilityDefault')}
            </GradientButton>

            <GradientButton
              onClick={() => handleApplyToProductionUnit(selectedProfile?.id ?? null)}
              variant="success"
              size="sm"
              disabled={!selectedProductionUnit || !selectedProfile}
            >
              {t('sfrManagement.profile.action.applyToProductionUnit')}
            </GradientButton>

            <GradientButton
              onClick={() => handleApplyToProductionUnit(null)}
              variant="warning"
              size="sm"
              disabled={!selectedProductionUnit || !productionUnitAssignedProfile}
            >
              {t('sfrManagement.profile.action.clearProductionUnit')}
            </GradientButton>

            <GradientButton
              onClick={() => handleApplyToLot(selectedProfile?.id ?? null)}
              variant="success"
              size="sm"
              disabled={!selectedLot || !selectedProfile || !selectedLotIsInteractive}
            >
              {t('sfrManagement.profile.action.applyToLot')}
            </GradientButton>

            <GradientButton
              onClick={() => handleApplyToLot(null)}
              variant="warning"
              size="sm"
              disabled={!selectedLot || !lotAssignedProfile || !selectedLotIsInteractive}
            >
              {t('sfrManagement.profile.action.clearLot')}
            </GradientButton>
          </div>
        </div>
      </div>

      {matrixStatus && (
        <div aria-live="polite" role="status" style={sfrStatusStyle(matrixStatus.tone)}>
          {matrixStatus.message}
        </div>
      )}

      {/* Test Calculator */}
      <div style={{
        backgroundColor: 'white',
        border: '1px solid #e5e7eb',
        borderRadius: '12px',
        padding: '20px',
        marginBottom: '20px',
        boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)'
      }}>
        <h3 style={{
          fontSize: '16px',
          fontWeight: '600',
          color: '#1f2937',
          margin: '0 0 16px 0'
        }}>
          {t('sfrManagement.calculator.title')}
        </h3>

        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '12px',
          flexWrap: 'wrap'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <label style={{ fontSize: '14px', fontWeight: '500', color: '#374151' }}>
              {t('sfrManagement.calculator.weightLabel')}
            </label>
            <input
              type="number"
              step="0.1"
              value={testWeight}
              onChange={(e) => setTestWeight(e.target.value)}
              placeholder={t('sfrManagement.calculator.weightPlaceholder')}
              style={{
                padding: '8px 12px',
                border: '1px solid #d1d5db',
                borderRadius: '6px',
                fontSize: '14px',
                width: '110px'
              }}
            />
            <span style={{ fontSize: '14px', color: '#6b7280' }}>g</span>
          </div>
          
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <label style={{ fontSize: '14px', fontWeight: '500', color: '#374151' }}>
              {t('sfrManagement.calculator.temperatureLabel')}
            </label>
            <input
              type="number"
              step="0.1"
              value={testTemperature}
              onChange={(e) => setTestTemperature(e.target.value)}
              placeholder={t('sfrManagement.calculator.temperaturePlaceholder')}
              style={{
                padding: '8px 12px',
                border: '1px solid #d1d5db',
                borderRadius: '6px',
                fontSize: '14px',
                width: '100px'
              }}
            />
            <span style={{ fontSize: '14px', color: '#6b7280' }}>°C</span>
          </div>
          
          <GradientButton onClick={calculateTestSFR} variant="info" size="sm">
            {t('sfrManagement.calculator.calculate')}
          </GradientButton>
          
          {calculatedSFR !== null && (
            <div style={{
              padding: '8px 16px',
              backgroundColor: '#dbeafe',
              border: '1px solid #3b82f6',
              borderRadius: '6px',
              fontSize: '14px',
              fontWeight: '500',
              color: '#1e40af'
            }}>
              {t('sfrManagement.calculator.result', { value: calculatedSFR.toFixed(2).replace('.', ',') })}
            </div>
          )}
        </div>

        {calculatedSFR !== null && calculatorDebugInfo && (
          <div
            style={{
              marginTop: '12px',
              padding: '12px 14px',
              backgroundColor: '#f8fafc',
              border: '1px solid #dbeafe',
              borderRadius: '8px',
              fontSize: '12px',
              color: '#475569'
            }}
          >
            <div style={{ fontWeight: 700, color: '#1e3a8a', marginBottom: '6px' }}>
              {t('sfrManagement.calculator.diagnosticsTitle')}
            </div>
            <div>
              {t('sfrManagement.calculator.diagnosticsCurrent', {
                temperature: calculatorDebugInfo.appliedEffectiveTemperature.toFixed(2).replace(/\.00$/, '')
              })}
            </div>
            {selectedPresetKey !== 'global'
              && calculatorDebugInfo.referenceSfr !== null
              && Math.abs(calculatorDebugInfo.referenceSfr - calculatedSFR) > 0.005 && (
                <div style={{ marginTop: '4px' }}>
                  {t('sfrManagement.calculator.diagnosticsReference', {
                    profile: selectedSpeciesLabel,
                    temperature: calculatorDebugInfo.referenceEffectiveTemperature?.toFixed(2).replace(/\.00$/, '') ?? '0',
                    value: calculatorDebugInfo.referenceSfr.toFixed(2).replace('.', ',')
                  })}
                </div>
              )}
          </div>
        )}
        
        <p style={{
          margin: '12px 0 0 0',
          fontSize: '12px',
          color: '#6b7280'
        }}>
          {t('sfrManagement.calculator.help')}
        </p>
      </div>

      {/* SFR Matrix */}
      <SFRMatrix
        onSFRChange={handleSFRChange}
        onStatusChange={setMatrixStatus}
        species={selectedPresetKey}
        refreshKey={refreshKey}
        externalMatrix={isEditingFacilityProfile ? currentSFRData ?? undefined : undefined}
        externalMatrixVersion={isEditingFacilityProfile ? matrixVersion : undefined}
        persistenceMode={isEditingFacilityProfile ? 'external' : 'species'}
      />

      {currentSFRData && (
        <div
          style={{
            backgroundColor: 'white',
            border: '1px solid #e5e7eb',
            borderRadius: '12px',
            padding: '20px',
            marginTop: '20px',
            boxShadow: '0 1px 3px rgba(0, 0, 0, 0.08)'
          }}
        >
          <h3
            style={{
              fontSize: '16px',
              fontWeight: 600,
              color: '#1f2937',
              margin: '0 0 16px 0'
            }}
          >
            {t('sfrManagement.tools.title')}
          </h3>

          <div
            style={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: '24px'
            }}
          >
            <div style={{ flex: '1 1 280px' }}>
              <h4
                style={{
                  fontSize: '14px',
                  fontWeight: 600,
                  color: '#1f2937',
                  margin: '0 0 12px 0'
                }}
              >
                {t('sfrManagement.tools.weightRows')}
              </h4>

              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  marginBottom: '12px'
                }}
              >
                <input
                  type="number"
                  step="0.1"
                  value={newWeight}
                  onChange={(event) => setNewWeight(event.target.value)}
                  placeholder={t('sfrManagement.tools.addWeightPlaceholder')}
                  style={{
                    flex: '1 1 auto',
                    padding: '8px 12px',
                    border: '1px solid #d1d5db',
                    borderRadius: '6px',
                    fontSize: '14px'
                  }}
                />
                <GradientButton onClick={handleAddWeight} variant="info" size="xs">
                  {t('sfrManagement.tools.addRow')}
                </GradientButton>
              </div>

              <ul
                style={{
                  listStyle: 'none',
                  padding: 0,
                  margin: 0,
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '8px'
                }}
              >
                {sortedWeights.map((weight, index) => (
                  <li
                    key={`${weight}-${index}`}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      backgroundColor: '#f9fafb',
                      border: '1px solid #e5e7eb',
                      borderRadius: '6px',
                      padding: '8px 12px'
                    }}
                  >
                    <span style={{ fontSize: '13px', color: '#1f2937' }}>{weight} g</span>
                    <GradientButton
                      onClick={() => handleRemoveWeight(index)}
                      variant="danger"
                      size="xxs"
                    >
                      {t('sfrManagement.tools.remove')}
                    </GradientButton>
                  </li>
                ))}
              </ul>
            </div>

            <div style={{ flex: '1 1 280px' }}>
              <h4
                style={{
                  fontSize: '14px',
                  fontWeight: 600,
                  color: '#1f2937',
                  margin: '0 0 12px 0'
                }}
              >
                {t('sfrManagement.tools.temperatureColumns')}
              </h4>

              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  marginBottom: '12px'
                }}
              >
                <input
                  type="number"
                  step="0.1"
                  value={newTemperature}
                  onChange={(event) => setNewTemperature(event.target.value)}
                  placeholder={t('sfrManagement.tools.addTemperaturePlaceholder')}
                  style={{
                    flex: '1 1 auto',
                    padding: '8px 12px',
                    border: '1px solid #d1d5db',
                    borderRadius: '6px',
                    fontSize: '14px'
                  }}
                />
                <GradientButton onClick={handleAddTemperature} variant="info" size="xs">
                  {t('sfrManagement.tools.addColumn')}
                </GradientButton>
              </div>

              <ul
                style={{
                  listStyle: 'none',
                  padding: 0,
                  margin: 0,
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '8px'
                }}
              >
                {sortedTemperatures.map((temperature, index) => (
                  <li
                    key={`${temperature}-${index}`}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      backgroundColor: '#f9fafb',
                      border: '1px solid #e5e7eb',
                      borderRadius: '6px',
                      padding: '8px 12px'
                    }}
                  >
                    <span style={{ fontSize: '13px', color: '#1f2937' }}>{temperature} °C</span>
                    <GradientButton
                      onClick={() => handleRemoveTemperature(index)}
                      variant="danger"
                      size="xxs"
                    >
                      {t('sfrManagement.tools.remove')}
                    </GradientButton>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      )}
      
      {/* Instructions */}
      <div className="form-card">
        <h4 style={{ marginTop: 0 }}>{t('sfrManagement.usage.title')}</h4>
        <ul style={{ margin: 0, paddingLeft: 20, fontSize: 14 }}>
          <li>{t('sfrManagement.usage.item1')}</li>
          <li>{t('sfrManagement.usage.item2')}</li>
          <li>{t('sfrManagement.usage.item3')}</li>
          <li>{t('sfrManagement.usage.item4')}</li>
          <li>{t('sfrManagement.usage.item5')}</li>
        </ul>
      </div>
    </div>
  )
}